"use client";

import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, type LucideIcon } from "lucide-react";
import { Button } from "./ui";
import { playActionConfirm } from "@/lib/sounds";

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: "danger" | "warning";
  confirmDisabled?: boolean;
  icon?: LucideIcon;
  onConfirm: () => void;
  onCancel: () => void;
  children?: React.ReactNode;
};

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Yes, remove",
  cancelLabel = "Cancel",
  variant = "danger",
  confirmDisabled,
  icon: Icon = AlertTriangle,
  onConfirm,
  onCancel,
  children,
}: ConfirmDialogProps) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="confirm-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-[4px]"
            onClick={onCancel}
          />
          <motion.div
            key="confirm-content"
            initial={{ opacity: 0, scale: 0.95, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 8 }}
            transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
            className="fixed inset-0 z-[71] flex items-center justify-center p-4"
            onClick={(e) => e.target === e.currentTarget && onCancel()}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label={title}
              className="w-full max-w-sm bg-surface rounded-lg overflow-hidden"
              style={{ border: "1px solid var(--glass-border)" }}
            >
              <div className="px-5 py-4">
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 mt-0.5 p-1.5 rounded-full ${variant === "danger" ? "bg-danger/10" : "bg-warning/10"}`}>
                    <Icon className={`w-4 h-4 ${variant === "danger" ? "text-danger" : "text-warning"}`} />
                  </div>
                  <div>
                    <h3 className="text-[14px] font-semibold text-foreground">{title}</h3>
                    <p className="text-[12px] text-muted mt-1 leading-relaxed">{message}</p>
                  </div>
                </div>
                {children && <div className="mt-3">{children}</div>}
              </div>
              <div
                className="px-5 py-3 flex items-center justify-end gap-2"
                style={{ borderTop: "1px solid var(--border)" }}
              >
                <Button variant="ghost" size="sm" onClick={onCancel}>
                  {cancelLabel}
                </Button>
                <Button variant="danger" size="sm" onClick={() => { playActionConfirm(); onConfirm(); }} disabled={confirmDisabled}>
                  {confirmLabel}
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
